// src/pages/FundPlanner/components/Portfolios/Main/OverviewReport.jsx
import React from "react";
import { getPortfolioPeriodOpeningDate } from "../portfolioPeriodDates";

/* ────────────────────────────────
   Helpers
──────────────────────────────── */
function formatCurrency(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value, digits = 2) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatYears(value, digits = 2) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)} yrs`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

function formatPeriodValue(value, format = "currency") {
  if (format === "percent") return formatPercent(value);
  if (format === "years") return formatYears(value);
  return formatCurrency(value);
}

function PeriodValue({ value, format }) {
  return <span className="text-app">{formatPeriodValue(value, format)}</span>;
}

function KpiTile({ label, value, description, valueClassName = "text-app" }) {
  return (
    <div
      className="rounded-lg border border-border bg-app p-3"
      title={description || ""}
    >
      <div className="text-xs text-muted">{label}</div>
      <div className={`mt-1 text-lg font-semibold leading-tight ${valueClassName}`}>
        {value}
      </div>
    </div>
  );
}

function PeriodTable({ title, columns, rows }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between bg-[hsl(var(--brand1-static))] px-3 py-2 text-sm font-semibold text-white">
        <span>{title}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs text-muted">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-3 py-2 font-medium ${
                    column.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.key}
                className={`${index % 2 === 1 ? "bg-muted/30" : "bg-app"} ${
                  row.emphasis ? "font-semibold" : ""
                }`}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-3 py-2 ${
                      column.align === "right" ? "text-right tabular-nums" : ""
                    }`}
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PeriodOverview({ periodOverview }) {
  const openingDate = getPortfolioPeriodOpeningDate(periodOverview?.startDate);
  const portfolioColumns = [
    { key: "label", label: "Metric" },
    {
      key: "beginning",
      label: `As of ${formatDate(openingDate)} EOD`,
      align: "right",
      render: (row) => <PeriodValue value={row.beginning} format={row.format} />,
    },
    {
      key: "ending",
      label: `As of ${formatDate(periodOverview?.endDate)} EOD`,
      align: "right",
      render: (row) => <PeriodValue value={row.ending} format={row.format} />,
    },
    {
      key: "change",
      label: "Change",
      align: "right",
      render: (row) => <PeriodValue value={row.change} format={row.format} />,
    },
  ];

  const activityColumns = [
    { key: "label", label: "Metric" },
    {
      key: "value",
      label: "Amount",
      align: "right",
      render: (row) => <PeriodValue value={row.value} />,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(24rem,0.75fr)]">
      <PeriodTable
        title="Portfolio Summary"
        columns={portfolioColumns}
        rows={periodOverview?.portfolioRows || []}
      />
      <PeriodTable
        title="Income and Activity Summary"
        columns={activityColumns}
        rows={periodOverview?.activityRows || []}
      />
    </div>
  );
}

/* ────────────────────────────────
   Component
──────────────────────────────── */
function SnapshotOverview({ metrics, reportMode = "single" }) {
  const isPeriodReport = reportMode === "period";

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <KpiTile
        label="Par/Face Amount"
        value={formatCurrency(metrics?.totalPar)}
      />
      <KpiTile
        label="Market Value"
        value={formatCurrency(metrics?.totalMarketValue)}
      />
      <KpiTile
        label="Book Value"
        value={formatCurrency(metrics?.totalBookValue)}
        description="Straight-line amortized or accreted book value, unless an override exists."
      />
      <KpiTile
        label="Accrued Interest"
        value={formatCurrency(metrics?.totalAccruedInterest)}
      />
      <KpiTile
        label="Market Value + Accrued"
        value={formatCurrency(metrics?.totalMarketValuePlusAccrued)}
      />
      <KpiTile
        label="Original Cost"
        value={formatCurrency(metrics?.totalOriginalCost)}
      />
      <KpiTile
        label="Unrealized Gain/Loss"
        value={formatCurrency(metrics?.totalUnrealizedGainLoss)}
      />
      {isPeriodReport ? (
        <KpiTile
          label="Effective Yield"
          value={formatPercent(metrics?.avgYieldToWorst)}
          description="Weighted by market value."
        />
      ) : null}
      <KpiTile
        label="Wtd Avg Maturity (Yr)"
        value={formatYears(metrics?.wamYears)}
        description="Weighted by par amount using effective maturity."
      />
    </div>
  );
}

export default function OverviewReport({ metrics, reportMode = "single", periodOverview = null }) {
  const openingDate = getPortfolioPeriodOpeningDate(periodOverview?.startDate);

  return (
    <div className="space-y-5">
      <SnapshotOverview metrics={metrics} reportMode={reportMode} />

      {reportMode === "period" && periodOverview ? (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-app">Period Metrics</div>
            <div className="mt-0.5 text-xs text-muted">
              {formatDate(openingDate)} EOD to {formatDate(periodOverview.endDate)} EOD
            </div>
          </div>
          <PeriodOverview periodOverview={periodOverview} />
        </div>
      ) : null}
    </div>
  );
}
